import { useEffect, useState } from "react";
import {
  fetchTypeTagCounts,
  type TypeTagCount,
  type TypeTagScope,
} from "../lib/type-tags";
import type { PocaConfig } from "../types";

type State = {
  counts: TypeTagCount[];
  total: number;
  /** 아직 페이지를 받는 중 (칩은 이미 보이되 숫자가 늘어날 수 있음) */
  loading: boolean;
  error: string | null;
};

const EMPTY: State = { counts: [], total: 0, loading: false, error: null };

/**
 * 현재 범위(그룹 · 멤버 · 재고)의 종류(TYPE) 태그별 건수.
 * 페이지가 도착할 때마다 중간 집계로 갱신되고, 다 받으면 loading=false.
 * enabled=false 면 아무것도 안 한다 (상세 화면, 컬렉션 로딩 중 등).
 */
export function useTypeTagCounts(
  config: PocaConfig,
  scope: TypeTagScope & { enabled: boolean }
) {
  const [state, setState] = useState<State>(EMPTY);
  const { collectionId, memberTag, availableOnly, enabled } = scope;

  useEffect(() => {
    if (!enabled) return;

    let cancelled = false;
    const ac = new AbortController();
    setState((s) => ({ ...s, loading: true, error: null }));

    fetchTypeTagCounts(
      config,
      { collectionId, memberTag, availableOnly },
      ac.signal,
      (partial) => {
        if (cancelled) return;
        setState({ counts: partial.counts, total: partial.total, loading: true, error: null });
      }
    )
      .then((result) => {
        if (cancelled) return;
        setState({ counts: result.counts, total: result.total, loading: false, error: null });
      })
      .catch((e) => {
        if (cancelled || ac.signal.aborted) return;
        console.warn("[poca] type tag counts failed", e);
        setState({
          counts: [],
          total: 0,
          loading: false,
          error: e instanceof Error ? e.message : "Failed to load types",
        });
      });

    return () => {
      cancelled = true;
      ac.abort();
    };
  }, [config, collectionId, memberTag, availableOnly, enabled]);

  return state;
}
